// src/utils/basicBehaviorLogger.js
// Phase 0: Dead simple behavioral data collection for localStorage
// Tracks basic user actions: play, skip, complete, remove

import logger from '../services/logger';

const BEHAVIOR_KEY = 'phantom_user_behavior';
const MAX_ACTIONS = 1000; // Keep only last 1000 actions for storage management

/**
 * Log a user action to localStorage
 * @param {Object} actionData - The action data to log
 * @param {string} actionData.action - Action type: 'play', 'skip', 'complete', 'remove'
 * @param {string} actionData.trackUri - Spotify track URI
 * @param {string} actionData.trackName - Track name (optional but helpful)
 * @param {string} actionData.artist - Artist name (optional but helpful)
 * @param {number} actionData.position - Position in track (ms) when action occurred
 * @param {number} actionData.timestamp - When action occurred (Date.now())
 * @param {string} actionData.sessionId - Current session ID (optional)
 */
export const logUserAction = (actionData) => {
  try {
    // Validate required fields
    if (!actionData.action || !actionData.timestamp) {
      logger.warn('Behavior logging: Missing required fields (action, timestamp)');
      return false;
    }

    // Get existing behavior data from localStorage
    const existing = JSON.parse(localStorage.getItem(BEHAVIOR_KEY) || '[]');
    
    // Create new action entry with unique ID
    const newAction = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Unique ID
      action: actionData.action,
      trackUri: actionData.trackUri || null,
      trackName: actionData.trackName || null,
      artist: actionData.artist || null,
      position: actionData.position || null, // Position in track (ms)
      timestamp: actionData.timestamp,
      sessionId: actionData.sessionId || generateSessionId()
    };

    // Add to existing data
    existing.push(newAction);

    // Keep only last MAX_ACTIONS to manage storage
    const recentActions = existing.slice(-MAX_ACTIONS);

    // Save back to localStorage
    localStorage.setItem(BEHAVIOR_KEY, JSON.stringify(recentActions));

    // Success logging
    logger.success(`Behavior logged: ${newAction.action} - ${newAction.trackName || 'Unknown'} at ${new Date(newAction.timestamp).toLocaleTimeString()}`);
    
    return true;

  } catch (error) {
    logger.error('Behavior logging failed:', error.message);
    return false;
  }
};

/**
 * Get all user behavior data from localStorage
 * @returns {Array} Array of user actions
 */
export const getUserBehavior = () => {
  try {
    const data = JSON.parse(localStorage.getItem(BEHAVIOR_KEY) || '[]');
    return data;
  } catch (error) {
    logger.error('Failed to retrieve user behavior data:', error.message);
    return [];
  }
};

/**
 * Get basic behavior statistics
 * @returns {Object} Basic stats about user behavior
 */
export const getBehaviorStats = () => {
  try {
    const data = getUserBehavior();
    
    if (data.length === 0) {
      return {
        totalActions: 0,
        actionTypes: {},
        oldestAction: null,
        newestAction: null
      };
    }

    // Count action types
    const actionTypes = data.reduce((acc, action) => {
      acc[action.action] = (acc[action.action] || 0) + 1;
      return acc;
    }, {});

    return {
      totalActions: data.length,
      actionTypes,
      oldestAction: new Date(Math.min(...data.map(a => a.timestamp))),
      newestAction: new Date(Math.max(...data.map(a => a.timestamp)))
    };

  } catch (error) {
    logger.error('Failed to get behavior stats:', error.message);
    return { error: error.message };
  }
};

/**
 * Get all actions since last sync and clear them from localStorage.
 * Used by useAutoQueue to flush behavior to the recommendation engine backend.
 * Tracks a sync index so only new actions are returned.
 * @returns {Array} Array of unsent user actions
 */
const SYNC_INDEX_KEY = 'phantom_behavior_sync_index';

export const getAndClearPendingActions = () => {
  try {
    const data = JSON.parse(localStorage.getItem(BEHAVIOR_KEY) || '[]');
    const lastSyncIndex = parseInt(localStorage.getItem(SYNC_INDEX_KEY) || '0', 10);

    // Get actions after the last sync index
    const pending = data.slice(lastSyncIndex);

    if (pending.length > 0) {
      // Update sync index to current length
      localStorage.setItem(SYNC_INDEX_KEY, String(data.length));
      logger.debug(`Behavior sync: returning ${pending.length} pending actions (index ${lastSyncIndex} → ${data.length})`);
    }

    return pending;
  } catch (error) {
    logger.error('Failed to get pending actions:', error.message);
    return [];
  }
};

/**
 * Clear all behavior data (for testing or reset)
 */
export const clearBehaviorData = () => {
  try {
    localStorage.removeItem(BEHAVIOR_KEY);
    localStorage.removeItem(SYNC_INDEX_KEY);
    logger.debug('Behavior data cleared');
    return true;
  } catch (error) {
    logger.error('Failed to clear behavior data:', error.message);
    return false;
  }
};

/**
 * Generate a simple session ID based on current time
 * Sessions are considered separate if more than 30 minutes apart
 */
const generateSessionId = () => {
  const now = new Date();
  const sessionStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 
                               Math.floor(now.getMinutes() / 30) * 30); // Round to 30-minute blocks
  return `session_${sessionStart.getTime()}`;
};

/**
 * Export behavior data as JSON (for potential future MongoDB export)
 * @returns {string} JSON string of all behavior data
 */
export const exportBehaviorData = () => {
  try {
    const data = getUserBehavior();
    return JSON.stringify(data, null, 2);
  } catch (error) {
    logger.error('Failed to export behavior data:', error.message);
    return null;
  }
};

// Development/Debug helpers (only in development)
if (process.env.NODE_ENV === 'development') {
  // Make functions available in browser console for debugging
  window.phantomBehaviorLogger = {
    logUserAction,
    getUserBehavior,
    getBehaviorStats,
    clearBehaviorData,
    exportBehaviorData
  };
  
  logger.debug('Phantom Behavior Logger loaded (Dev mode)');
  logger.debug('Access via: window.phantomBehaviorLogger');
}